import { useCallback, useEffect, useRef, useState } from "react"
import {
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  getMultiFactorResolver,
  TotpMultiFactorGenerator,
} from "firebase/auth"
import { callCloudFunctionNoAuth } from "../../../helpers/cloudFunctionRequests"
import {
  clearRecaptcha,
  getRecaptcha,
  waitForRecaptchaReady,
} from "../../../hooks/captcha/recaptchaSingleton"
import { LogDebug, LogError, LogInfo } from "../../../helpers/logger"

const RECAPTCHA_REQUEST_TIMEOUT_MS = 30000
const SMS_VERIFY_REQUEST_DEADLINE_MS = 30000
const invalidVerifierRegex =
  /invalid application verifier|reCAPTCHA token response is either invalid or expired|captcha check failed|closed by the user|cancelled|dismissed|aborted/i

const isInvalidVerifierError = (error) =>
  error?.code === "auth/invalid-app-credential" ||
  error?.code === "auth/captcha-check-failed" ||
  error?.code === "auth/recaptcha-render-timeout" ||
  invalidVerifierRegex.test(error?.message || "")

export default function useLoginMfa({
  auth,
  recaptchaVerifier,
  recaptchaReady,
  recaptchaInitError,
  userEmail,
  setErr,
  getAuthErrorMessage,
  onTotpChallengeTimeout,
}) {
  const [has2FA, setHas2FA] = useState(false)
  const [resolver, setResolver] = useState(null)
  const [twoFAMethod, setTwoFAMethod] = useState("")

  const [verificationId, setVerificationId] = useState(null)
  const [verificationSMSCode, setVerificationSMSCode] = useState("")
  const [isResending, setIsResending] = useState(false)
  const [pendingAutoSms, setPendingAutoSms] = useState(false)
  const [autoSmsNeedsManualRequest, setAutoSmsNeedsManualRequest] = useState(false)
  const smsRequestIdRef = useRef(0)
  const smsRequestDeadlineTimeoutRef = useRef(null)

  const [verificationTOTPCode, setVerificationTOTPCode] = useState("")

  const clearSmsRequestDeadline = (requestId = null) => {
    // When requestId is provided, only the active request can clear its timer.
    if (
      requestId !== null &&
      smsRequestIdRef.current !== requestId
    ) {
      return
    }

    if (smsRequestDeadlineTimeoutRef.current) {
      clearTimeout(smsRequestDeadlineTimeoutRef.current)
      smsRequestDeadlineTimeoutRef.current = null
    }
  }

  useEffect(() => {
    return () => {
      smsRequestIdRef.current += 1
      clearSmsRequestDeadline()
    }
  }, [])

  const requestSmsCode = useCallback(async (options = {}) => {
    const { suppressUnavailableError = false, isAutoRequest = false } = options

    if (!resolver) return
    if (isResending) return

    let activeRecaptchaVerifier = recaptchaVerifier

    try {
      activeRecaptchaVerifier = getRecaptcha(auth) || recaptchaVerifier
    } catch (error) {
      LogError("Failed to acquire reCAPTCHA verifier for SMS request:", error)
    }

    if (!activeRecaptchaVerifier) {
      if (!suppressUnavailableError) {
        setErr(
          "Security verification is unavailable right now. Please refresh and try again.",
        )
      }
      return
    }

    const phoneAuthProvider = new PhoneAuthProvider(auth)
    const getMultiFactorHint = resolver.hints.find(
      ({ factorId }) => factorId === "phone",
    )
    const phoneInfoOptions = {
      multiFactorHint: getMultiFactorHint,
      session: resolver.session,
    }

    const requestId = smsRequestIdRef.current + 1
    smsRequestIdRef.current = requestId

    try {
      LogDebug("SMS code request started", { requestId, isAuto: isAutoRequest })
      setAutoSmsNeedsManualRequest(false)
      setIsResending(true)

      let nextVerificationId = null
      let requestError = null

      for (let attempt = 1; attempt <= 2; attempt += 1) {
        activeRecaptchaVerifier = recaptchaVerifier

        try {
          activeRecaptchaVerifier = getRecaptcha(auth) || recaptchaVerifier
          await waitForRecaptchaReady(auth, RECAPTCHA_REQUEST_TIMEOUT_MS)

          if (smsRequestIdRef.current !== requestId) {
            LogDebug(
              "Ignoring stale SMS code request before Firebase verify call",
              {
                requestId,
                attempt,
              },
            )
            return
          }
        } catch (error) {
          if (smsRequestIdRef.current !== requestId) {
            LogDebug("Ignoring stale SMS code request during reCAPTCHA readiness", {
              requestId,
              attempt,
            })
            return
          }

          LogError("Failed to acquire ready reCAPTCHA verifier for SMS request:", error)

          requestError = error
          if (isInvalidVerifierError(error) && attempt === 1) {
            if (smsRequestIdRef.current !== requestId) {
              LogDebug("Ignoring stale SMS code request before reCAPTCHA reset", {
                requestId,
                attempt,
              })
              return
            }

            clearRecaptcha()
            continue
          }

          break
        }

        if (!activeRecaptchaVerifier) {
          requestError = new Error("RECAPTCHA_UNAVAILABLE")
          requestError.code = "auth/recaptcha-unavailable"
          break
        }

        try {
          // Start a deadline for the Firebase verify call itself so dismissed
          // challenges cannot leave the request in a permanent busy state.
          clearSmsRequestDeadline()
          smsRequestDeadlineTimeoutRef.current = setTimeout(() => {
            if (smsRequestIdRef.current !== requestId) return

            // Invalidate this request and reset reCAPTCHA so retries are not blocked.
            smsRequestIdRef.current += 1
            smsRequestDeadlineTimeoutRef.current = null
            clearRecaptcha()
            setVerificationId(null)
            setAutoSmsNeedsManualRequest(true)
            setErr(
              "Security verification timed out. Please click Resend Code to try again.",
            )
            setIsResending(false)
          }, SMS_VERIFY_REQUEST_DEADLINE_MS)

          nextVerificationId = await phoneAuthProvider.verifyPhoneNumber(
            phoneInfoOptions,
            activeRecaptchaVerifier,
          )
          requestError = null
          break
        } catch (error) {
          requestError = error

          if (isInvalidVerifierError(error) && attempt === 1) {
            if (smsRequestIdRef.current !== requestId) {
              LogDebug("Ignoring stale SMS code request before retry reset", {
                requestId,
                attempt,
              })
              return
            }

            clearRecaptcha()
            continue
          }

          break
        } finally {
          clearSmsRequestDeadline(requestId)
        }
      }

      if (requestError) {
        throw requestError
      }

      // Ignore late completions from stale requests.
      if (smsRequestIdRef.current !== requestId) {
        LogDebug("Ignoring stale SMS code request success", { requestId })
        return
      }

      setVerificationId(nextVerificationId)
      setAutoSmsNeedsManualRequest(false)
      LogDebug("SMS code request completed", { requestId })
    } catch (error) {
      if (smsRequestIdRef.current !== requestId) {
        LogDebug("Ignoring stale SMS code request error", { requestId })
        return
      }

      if (isInvalidVerifierError(error)) {
        clearRecaptcha()
        setVerificationId(null)
        if (isAutoRequest) {
          setAutoSmsNeedsManualRequest(true)
        }
        setErr(
          "Security verification expired. Please click Resend Code to try again.",
        )
        return
      }

      if (error?.code === "auth/recaptcha-unavailable") {
        if (isAutoRequest) {
          setAutoSmsNeedsManualRequest(true)
        }
        if (!suppressUnavailableError) {
          setErr(
            "Security verification is still loading. Please wait a moment and try Resend Code.",
          )
        }
        return
      }

      LogError("Error during sending SMS verification code:", error)
      if (isAutoRequest) {
        setAutoSmsNeedsManualRequest(true)
      }
      setErr(getAuthErrorMessage(error, "Error requesting SMS verification code."))
    } finally {
      if (smsRequestIdRef.current === requestId) {
        clearSmsRequestDeadline()
        setIsResending(false)
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    auth,
    getAuthErrorMessage,
    isResending,
    recaptchaReady,
    recaptchaVerifier,
    resolver,
    setErr,
  ])

  useEffect(() => {
    if (!pendingAutoSms) return
    if (twoFAMethod !== "sms") return
    if (!resolver) return
    if (!recaptchaVerifier) return
    if (!recaptchaReady) return

    // Auto-send once when SMS MFA starts; reCAPTCHA may still challenge based on risk.
    setPendingAutoSms(false)
    requestSmsCode({ suppressUnavailableError: true, isAutoRequest: true })
  }, [
    pendingAutoSms,
    recaptchaReady,
    recaptchaVerifier,
    requestSmsCode,
    resolver,
    twoFAMethod,
  ])

  useEffect(() => {
    if (twoFAMethod === "sms" && recaptchaInitError) {
      setErr(
        "Security verification failed to load. Please refresh the page and try again.",
      )
    }
  }, [recaptchaInitError, setErr, twoFAMethod])

  const handleMFARequiredError = useCallback(
    async ({ multiFactorError }) => {
      setHas2FA(true)
      setIsResending(false)
      setVerificationId(null)
      setAutoSmsNeedsManualRequest(false)

      const resolverInstance = getMultiFactorResolver(auth, multiFactorError)
      setResolver(resolverInstance)

      LogDebug("Resolver Instance == ", resolverInstance)

      let preferredMethod = null

      if (resolverInstance.hints.length === 2) {
        try {
          const response = await callCloudFunctionNoAuth("checkMfaSettings", {
            email: userEmail,
          })

          preferredMethod = response.data.preferred_method
        } catch (error) {
          preferredMethod = resolverInstance.hints[1].factorId
          LogError("Error fetching preferred method:", error)
        }
      }

      LogDebug("Preferred Method == ", preferredMethod)

      const factorId = resolverInstance.hints[0].factorId

      if (preferredMethod) {
        if (preferredMethod === "phone") {
          preferredMethod = "sms"
        }
        LogDebug(`2FA method: ${preferredMethod}`)
        if (preferredMethod === "sms") {
          setPendingAutoSms(true)
        }
        setTwoFAMethod(preferredMethod)
        return
      }

      if (factorId === "phone") {
        LogDebug("2FA method: SMS")
        setPendingAutoSms(true)
        setTwoFAMethod("sms")
        return
      }

      if (factorId === "totp") {
        LogDebug("2FA method: TOTP")
        setTwoFAMethod("totp")
      }
    },
    [auth, userEmail],
  )

  const resolveSMS = useCallback(async () => {
    try {
      const cred = PhoneAuthProvider.credential(verificationId, verificationSMSCode)
      const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(cred)

      const userCredential = await resolver.resolveSignIn(multiFactorAssertion)

      LogInfo(
        "User successfully signed in with the second factor phone number.",
        userCredential,
      )
    } catch (error) {
      LogError("Error during multi-factor authentication:", error)
      setErr(
        getAuthErrorMessage(error, "Error during multi-factor authentication."),
      )
    }
  }, [
    getAuthErrorMessage,
    resolver,
    setErr,
    verificationId,
    verificationSMSCode,
  ])

  const resolveTOTP = useCallback(async () => {
    if (!verificationTOTPCode) {
      setErr("please enter the verification code.")
      return
    }

    const handleResolverUid = resolver.hints.find(
      ({ factorId }) => factorId === "totp",
    ).uid

    LogDebug("Resolver UID == ", handleResolverUid)

    const multiFactorAssertion = TotpMultiFactorGenerator.assertionForSignIn(
      handleResolverUid,
      verificationTOTPCode,
    )

    try {
      const userCredential = await resolver.resolveSignIn(multiFactorAssertion)
      LogInfo("Successfully signed in with the second factor TOTP", userCredential)
    } catch (error) {
      LogError("Error during TOTP multi-factor sign-in:", error)

      const totpChallengeTimeoutRegex = /totp challenge timeout, provide first factor again\.?/i
      const isTotpChallengeTimeout = totpChallengeTimeoutRegex.test(
        error?.message || "",
      )

      if (isTotpChallengeTimeout) {
        onTotpChallengeTimeout?.(
          "Your verification session expired. Please sign in again to continue.",
        )
        return
      }

      setErr(getAuthErrorMessage(error, "Unable to validate the verification code."))
    }
  }, [
    getAuthErrorMessage,
    onTotpChallengeTimeout,
    resolver,
    setErr,
    verificationTOTPCode,
  ])

  const resetMfaState = useCallback(() => {
    smsRequestIdRef.current += 1
    clearSmsRequestDeadline()
    setHas2FA(false)
    setResolver(null)
    setTwoFAMethod("")
    setPendingAutoSms(false)
    setAutoSmsNeedsManualRequest(false)
    setVerificationId(null)
    setVerificationSMSCode("")
    setVerificationTOTPCode("")
    setIsResending(false)
  }, [])

  const resetMfaBeforeSignIn = useCallback(() => {
    smsRequestIdRef.current += 1
    clearSmsRequestDeadline()
    setTwoFAMethod(null)
    setPendingAutoSms(false)
    setAutoSmsNeedsManualRequest(false)
    setVerificationId(null)
    setVerificationSMSCode("")
    setVerificationTOTPCode("")
    setIsResending(false)
  }, [])

  return {
    has2FA,
    resolver,
    twoFAMethod,
    setTwoFAMethod,
    verificationId,
    verificationSMSCode,
    setVerificationSMSCode,
    verificationTOTPCode,
    setVerificationTOTPCode,
    isResending,
    autoSmsNeedsManualRequest,
    requestSmsCode,
    handleMFARequiredError,
    resolveSMS,
    resolveTOTP,
    resetMfaState,
    resetMfaBeforeSignIn,
  }
}
